/**
 * FeatureGrantsSubPanel
 * ----------------------
 * Per-feature per-user grants manager rendered underneath each row of
 * `AdminFeatureVisibilityPanel`. Admin can add an existing user by email
 * (autocomplete against /api/admin/users/search) and revoke a grant with
 * a single click. Grants are ignored for admin users (they always see
 * every feature).
 *
 * Endpoints:
 *   GET    /api/admin/features/{key}/grants
 *   POST   /api/admin/features/{key}/grants   body: { email }
 *   DELETE /api/admin/features/{key}/grants/{user_id}
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { Loader2, UserPlus, X, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function FeatureGrantsSubPanel({ featureKey, featureLabel }) {
  const { token } = useAuth();
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [revokingId, setRevokingId] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/admin/features/${encodeURIComponent(featureKey)}/grants`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setGrants(r.data?.grants || []);
    } catch (e) {
      setGrants([]);
    } finally {
      setLoading(false);
    }
  }, [token, featureKey]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const clean = (email || '').trim();
    if (!clean) {
      toast.error('Enter a user email to grant');
      return;
    }
    setAdding(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/admin/features/${encodeURIComponent(featureKey)}/grants`,
        { email: clean },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (r.data?.already_admin) {
        toast.info(`${clean} is an admin — already has full access`);
      } else {
        toast.success(`${featureLabel} activated for ${clean}`);
        await load();
      }
      setEmail('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add grant');
    } finally {
      setAdding(false);
    }
  };

  const revoke = async (userId, userEmail) => {
    if (!window.confirm(`Revoke ${featureLabel} from ${userEmail}?`)) return;
    setRevokingId(userId);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/features/${encodeURIComponent(featureKey)}/grants/${encodeURIComponent(userId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Revoked from ${userEmail}`);
      setGrants((g) => g.filter((x) => x.user_id !== userId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to revoke grant');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div
      className="mt-2 p-3 rounded-md border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50/50"
      data-testid={`fgs-panel-${featureKey}`}
    >
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-amber-800 mb-2">
        <Sparkles className="w-3 h-3" />
        Per-user "Premium Preview" grants
      </div>

      {loading ? (
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500" data-testid={`fgs-loading-${featureKey}`}>
          <Loader2 className="w-3 h-3 animate-spin" /> Loading grants…
        </div>
      ) : grants.length === 0 ? (
        <p className="text-[11px] text-slate-500 italic" data-testid={`fgs-empty-${featureKey}`}>
          No users currently have this feature as a per-user premium preview.
        </p>
      ) : (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`fgs-chips-${featureKey}`}>
          {grants.map((g) => (
            <span
              key={g.user_id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-amber-300 text-[11px] text-amber-900 shadow-sm"
              data-testid={`fgs-chip-${featureKey}-${g.user_id}`}
              title={g.company ? `${g.email} — ${g.company}` : g.email}
            >
              <span className="font-medium">{g.email}</span>
              <button
                type="button"
                onClick={() => revoke(g.user_id, g.email)}
                disabled={revokingId === g.user_id}
                className="hover:bg-amber-100 rounded-full p-0.5 disabled:opacity-40"
                data-testid={`fgs-revoke-${featureKey}-${g.user_id}`}
                title="Revoke"
              >
                {revokingId === g.user_id
                  ? <Loader2 className="w-2.5 h-2.5 animate-spin" />
                  : <X className="w-2.5 h-2.5" />}
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-1.5">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="user@example.com"
          disabled={adding}
          className="flex-1 min-w-0 rounded-md border border-amber-300 px-2 py-1 text-[12px] placeholder:text-slate-400 focus:border-amber-500 focus:ring-amber-500"
          data-testid={`fgs-email-input-${featureKey}`}
        />
        <button
          type="button"
          onClick={add}
          disabled={adding || !email.trim()}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
          data-testid={`fgs-add-btn-${featureKey}`}
        >
          {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
          Activate
        </button>
      </div>
      <p className="text-[10px] text-slate-500 italic mt-1.5">
        Grantee sees the feature immediately, with a "Premium Preview" banner distinguishing it from a site-wide release.
      </p>
    </div>
  );
}
